import React, { useRef, useState } from 'react'
import { arrayOf, bool, number, shape, string } from 'prop-types'
import { css } from '@emotion/core'
import { Separator, useContainerWidth, H3 } from '@fs/zion-ui'
import NoDataNotice from './NoDataNotice'
import scaleCanvasForDpi from '../../../../../shared/helpers/scaleCanvasForDpi'
import LineChartPanel from './LineChartPanel'
import ComparisonPanel from '../ComparisonPanel/ComparisonPanel'
import { GRADIENT, NONE } from '../ComparisonPanel/ComparisonPanelBackgroundTypes'
import { useDSCState } from '../../../../../state/DSCStore'
import { useAamState } from '../../../state/AamStore'
import { getGender } from '../../../../../shared/helpers/person_Helper'
import { useComparisonByYear } from '../../../hooks/hooks'

export default function LineChartComparison({
  year,
  comparisonYear,
  id,
  comparisonTargetPerson,
  header,
  highlightColor,
  tileContentData,
  headingWithIcon,
  tileData,
}) {
  const tileContent = tileContentData?.length ? tileContentData : null
  const supertext = tileContent?.[0]?.textTitle ?? ''
  const canvasRef = useRef()
  const [hasData, setHasData] = useState(true)

  const atWidth = useContainerWidth()
  const dscState = useDSCState()
  const aamState = useAamState()
  const showComparison = useComparisonByYear(aamState, comparisonTargetPerson, dscState, tileData, comparisonYear)

  const layoutSizing = {
    verticalChartPaddingTop: 150,
    horizontalChartPaddingLeft: '5%',
    horizontalChartPaddingRight: '5%',
    height: atWidth({ default: 300, md: 450, xl: 500 }),
    textLayout: {
      calloutFont: {
        color: '#FFFFFF',
        font: atWidth({ default: '16px Museo', xl: '24px Museo' }),
      },
      attributionFont: {
        color: '#808080',
        font: '10px Verdana',
      },
    },
  }

  const getMaxValue = (lineCoords, prop) => {
    let max = 0
    for (let i = 0; i < lineCoords.length; i++) {
      if (lineCoords[i][prop] > max) {
        max = lineCoords[i][prop]
      }
    }
    return max
  }

  const getMinValue = (lineCoords, prop) => {
    let min = 99999999999
    for (let i = 0; i < lineCoords.length; i++) {
      if (lineCoords[i][prop] < min) {
        min = lineCoords[i][prop]
      }
    }
    return min
  }
  // eslint-disable-next-line no-warning-comments
  /*
  const formatLocalizedCurrency = (dataValue, locale = '') => {
    let retVal = dataValue.valText
    // eslint-disable-next-line no-warning-comments
    // TODO: possibly handle different formats based on user locale
    if (locale && locale === 'en-US') {
      retVal = dataValue.val.toLocaleString(locale, {
        style: 'currency',
        currency: 'USD',
      })
    }

    return retVal
  }
  */

  React.useEffect(() => {
    const resizeToParent = () => {
      canvasRef.current.style.width = '100%'
      canvasRef.current.width = canvasRef.current.scrollWidth
      canvasRef.current.height = layoutSizing.height
    }

    const drawCallOut = ({
      strokeColor,
      callOutX,
      callOutY,
      ctx,
      callOutValue,
      verticalOffset,
      secondValue = false,
    }) => {
      const textY = callOutY - verticalOffset > 20 ? callOutY - verticalOffset : 20

      ctx.strokeStyle = strokeColor
      ctx.lineWidth = 1
      ctx.fillStyle = layoutSizing.textLayout.calloutFont.color
      ctx.font = layoutSizing.textLayout.calloutFont.font
      ctx.textAlign = 'center'
      if (secondValue) {
        ctx.moveTo(callOutX, textY)
        ctx.fillText(callOutValue, callOutX, textY - 15)
      } else {
        ctx.moveTo(callOutX, textY + 10)
        ctx.fillText(callOutValue, callOutX, textY)
      }
      ctx.lineTo(callOutX, callOutY)
      ctx.stroke()
      ctx.beginPath()
      ctx.fillStyle = '#FFFFFF'
      ctx.strokeStyle = '#888888'
      ctx.arc(callOutX, callOutY, 6.5, 0, 2 * Math.PI, false)
      ctx.fill()
      ctx.stroke()
    }

    const draw = ({ imgCanvas, lineCoords, drawColor, callOutValue1, callOutValue2 }) => {
      const ctx = imgCanvas.getContext('2d')

      const { width: virtualWidth, height: virtualHeight } = imgCanvas.getBoundingClientRect()
      scaleCanvasForDpi(imgCanvas, ctx)

      ctx.strokeStyle = drawColor
      ctx.lineWidth = 5

      const maxV = getMaxValue(lineCoords, 'val')
      const maxXUnit = getMaxValue(lineCoords, 'xUnit')
      const minXUnit = getMinValue(lineCoords, 'xUnit')

      // The populating values for padding could be in percent - hence the isNan check;
      const paddingInlineEnd = !Number.isNaN(Number(layoutSizing.horizontalChartPaddingRight))
        ? layoutSizing.horizontalChartPaddingRight
        : (layoutSizing.horizontalChartPaddingRight.split('%')[0] * virtualWidth) / 100

      const paddingInlineStart = !Number.isNaN(Number(layoutSizing.horizontalChartPaddingLeft))
        ? layoutSizing.horizontalChartPaddingLeft
        : (layoutSizing.horizontalChartPaddingLeft.split('%')[0] * virtualWidth) / 100

      const paddingTop = !Number.isNaN(Number(layoutSizing.verticalChartPaddingTop))
        ? layoutSizing.verticalChartPaddingTop
        : (layoutSizing.verticalChartPaddingTop.split('%')[0] * virtualHeight) / 100

      const heightRat = (virtualHeight - paddingTop) / maxV
      const widthRat = (virtualWidth - paddingInlineStart - paddingInlineEnd) / (maxXUnit - minXUnit)
      if (lineCoords.length > 0) {
        ctx.moveTo(widthRat * (lineCoords[0].xUnit - minXUnit) + paddingInlineStart, virtualHeight) // Start the charting x at the x of the first value vs 0
        ctx.beginPath()

        let curValY
        let curValX

        let callOutX
        let callOutY

        let callOutX2
        let callOutY2
        let data

        for (let i = 0; i < lineCoords.length; i++) {
          curValY = imgCanvas.scrollHeight - heightRat * lineCoords[i].val
          curValX = widthRat * (lineCoords[i].xUnit - minXUnit) + paddingInlineStart

          if (lineCoords[i]?.xUnit && callOutValue1) {
            if (lineCoords[i].xUnit.toString() === callOutValue1.toString()) {
              data = {
                val: lineCoords[i].val,
                valText: lineCoords[i].valText,
                valAttribution: lineCoords?.[i]?.attribution ?? '',
              }
              callOutX = curValX
              callOutY = curValY
            }
          }

          if (lineCoords[i].xUnit === callOutValue2) {
            callOutX2 = curValX
            callOutY2 = curValY
          }

          ctx.lineTo(curValX, curValY)
        }

        const selectedValue = data ?? {
          val: null,
          valText: '',
          valAttribution: '',
        }

        if (!selectedValue.val) {
          setHasData(false)
        } else {
          setHasData(true)
        }
        ctx.stroke()

        const attribution = selectedValue && selectedValue.valAttribution ? selectedValue.valAttribution : ''
        ctx.fillText(attribution, virtualWidth * 0.5, 20)

        drawCallOut({
          strokeColor: highlightColor,
          callOutX,
          callOutY,
          ctx,
          callOutValue: callOutValue1,
          verticalOffset: 60,
        })

        if (callOutValue2 !== callOutValue1) {
          // No need to draw the second one if the value is the same
          drawCallOut({
            strokeColor: highlightColor,
            callOutX: callOutX2,
            callOutY: callOutY2,
            ctx,
            callOutValue: callOutValue2,
            verticalOffset: 80,
            secondValue: true,
          })
        }
      }
    }

    const initializeView = (tc) => {
      let points = []
      if (tc?.length) {
        tc.forEach((item) => {
          points.push({
            xUnit: item.year,
            val: item.price,
            valText: item.textHeadline,
            attribution: item.attribution,
            interactive: true,
            // detailText: detailText
          })
        })
      }

      points = points.sort((a, b) => {
        return a.xUnit - b.xUnit
      })
      resizeToParent()
      draw({
        imgCanvas: canvasRef.current,
        lineCoords: points,
        drawColor: highlightColor,
        callOutValue1: year,
        callOutValue2: comparisonYear,
      })
    }

    initializeView(tileContent)
  }, [
    highlightColor,
    supertext,
    tileContent,
    year,
    comparisonYear,
    layoutSizing.height,
    layoutSizing.textLayout.calloutFont.color,
    layoutSizing.textLayout.calloutFont.font,
    layoutSizing.horizontalChartPaddingRight,
    layoutSizing.horizontalChartPaddingLeft,
    layoutSizing.verticalChartPaddingTop,
    layoutSizing.verticalChartPaddingBottom,
  ])

  const eventContainerStyle = css`
    display: flex;
    justify-content: center;
    align-items: ${atWidth({ default: 'center', lg: '' })};
    width: 100%;
    flex-direction: ${atWidth({ default: 'column-reverse', lg: 'row' })};
  `
  const rootLineChartComparisonStyle = css`
    display: flex;
    flex-direction: column;
    align-items: center;
    outline: none;
  `

  const background = showComparison ? GRADIENT : NONE // Don't show a background if there isn't a comparisonTarget, or the comparisonTarget is the user

  return (
    <div
      data-testid="line-chart-comparison"
      css={rootLineChartComparisonStyle}
      onKeyPress={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
      role="button"
      tabIndex="-1"
    >
      <Separator size="sm" />
      {/*
          Strangeness alert - each item comes back from the server with its own title
          */}
      {(!comparisonYear || comparisonYear === tileData?.year) && <H3 centered>{tileData.year}</H3>}
      <H3 centered>{headingWithIcon}</H3>
      <Separator size="md" />
      <div css={eventContainerStyle}>
        <ComparisonPanel usegradient gender={tileData?.gender?.toLowerCase()} background={background}>
          <LineChartPanel
            year={year}
            id={id}
            yearPrices={tileContentData}
            header={header}
            styling={{ padding: '10px' }}
            person={{ atn: tileData.atn, dob: year }}
            gender={tileData?.gender?.toLowerCase()}
            showPersonBlock={showComparison}
          />
        </ComparisonPanel>
        {showComparison && (
          <ComparisonPanel
            usegradient
            gender={
              comparisonTargetPerson?.gender
                ? comparisonTargetPerson?.gender.toLowerCase()
                : getGender(comparisonTargetPerson?.genderType)
            }
            background={GRADIENT}
          >
            <LineChartPanel
              id={id}
              year={comparisonYear}
              yearPrices={tileContentData}
              header={header}
              styling={{ padding: '10px' }}
              thumbnailIcon={comparisonTargetPerson?.portraitThumbnailLink ?? ''}
              person={comparisonTargetPerson}
              gender={
                comparisonTargetPerson?.gender
                  ? comparisonTargetPerson?.gender.toLowerCase()
                  : getGender(comparisonTargetPerson?.genderType)
              }
              showPersonBlock
            />
          </ComparisonPanel>
        )}
      </div>
      <canvas ref={canvasRef} id="canvas" css={{ maxWidth: '1400px' }} />
      {!hasData && <NoDataNotice />}
    </div>
  )
}

LineChartComparison.propTypes = {
  year: number,
  highlightColor: string,
  tileContentData: arrayOf(
    shape({
      complete: bool,
      error: bool,
      load: bool,
      pending: bool,
      res: arrayOf(
        shape({
          category: string,
          categoryId: number,
          icon: string,
          id: number,
          languageOnly: string,
          localeOnly: string,
          price: number,
          subcategory: string,
          textHeadline: string,
          textTitle: string,
          tileType: string,
          year: number,
        })
      ),
    })
  ),
  tileData: shape({
    personId: string,
    gender: string,
  }),
}
